/** How long an Undo stays offered (the kit's toastUndo default; plan §0.8). */
export const UNDO_MS = 6000;
/** The send-undo window for replies and DMs: the message waits this long before it goes to Instagram. */
export const SEND_UNDO_MS = 5000;

/**
 * Deferred commit for actions the server cannot reverse (reject an approval, send a reply): the screen changes at
 * once, the server is told after `ms` unless Undo is pressed. Timers live outside React, so leaving the page does not
 * lose the action; when the tab is hidden or closed every pending commit runs right away.
 */
const pending = new Map<string, { timer: ReturnType<typeof setTimeout>; commit: () => void }>();

export function deferCommit(key: string, commit: () => void, ms = UNDO_MS): { cancel: () => boolean; flush: () => void } {
  pending.get(key)?.commit(); // a second action on the same thing: the first one goes now
  const run = () => {
    const p = pending.get(key);
    if (!p) return;
    clearTimeout(p.timer);
    pending.delete(key);
    p.commit();
  };
  pending.set(key, { timer: setTimeout(run, ms), commit });
  return {
    cancel: () => {
      const p = pending.get(key);
      if (!p) return false;
      clearTimeout(p.timer);
      pending.delete(key);
      return true;
    },
    flush: run,
  };
}

export const hasPendingCommit = (key: string) => pending.has(key);

/** Run every pending commit now (tab hidden or closing; also used by tests). */
export function flushPendingCommits() {
  for (const [key, p] of [...pending]) {
    clearTimeout(p.timer);
    pending.delete(key);
    p.commit();
  }
}

if (typeof window !== "undefined") window.addEventListener("pagehide", flushPendingCommits);
